import React, {FC} from 'react'
import {Formik, Form, Field, FieldProps} from 'formik'
import {ReactSelectInput} from '../../../../../_metronic/partials/controls'
import {useIntl} from 'react-intl'
import useClasses from '../../../../../hooks/useClasses'
import useSubjects from '../../../../../hooks/useSubjects'
import useEstablishements from '../../../../../hooks/useEstablishements'
import {RegisterUserRequest} from '../../../../../redux/reducers/auth/types'

export interface submitClassInformationFormProps {
  classrooms: string[]
  subjects: string[]
  establishements: string[]
}
interface ClassInformationProps {
  onSubmit: (vals: submitClassInformationFormProps) => void
  user: RegisterUserRequest
  btnRef: any
  error: string
}

const ClassInformation: FC<ClassInformationProps> = ({user, onSubmit, btnRef, error}) => {
  const {classes} = useClasses()
  const {establishements} = useEstablishements()
  const {subjects} = useSubjects()

  const getClassesValue = (value: any) => {
    let res: {value: string; label: string}[] = []
    value.forEach((val: any) => {
      let target = classes.find((est) => est._id == val)
      if (target) {
        res.push({value: target._id!, label: target.name})
      }
    })
    return res
  }

  const getEstablishementsValue = (value: any) => {
    let res: {value: string; label: string}[] = []
    value.forEach((val: any) => {
      let target = establishements.find((est) => est._id == val)
      if (target) {
        res.push({value: target._id!, label: target.name})
      }
    })
    return res
  }

  const getSubjectsValue = (value: any) => {
    let res: {value: string; label: string}[] = []
    value.forEach((val: any) => {
      let target = subjects.find((subject) => subject._id == val)
      if (target) {
        res.push({value: target._id!, label: target.name})
      }
    })
    return res
  }

  const intl = useIntl()

  return (
    <Formik
      initialValues={{
        establishements: user.establishements ? user.establishements : [],
        classrooms: user.classrooms ? user.classrooms : [],
        subjects: user.subjects ? user.subjects : [],
        role: user.role,
      }}
      onSubmit={(vals) => {
        onSubmit(vals)
      }}
    >
      {({handleSubmit, values, setValues, setFieldValue, errors}) => (
        <Form>
          <div className='row'>
            <div className='col-sm'></div>
            <h2 className='fw-bolder d-flex align-items-center text-dark'>
              {intl.formatMessage({id: 'MENU.CLASSE_INFORMATION'})}
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title='Billing is issued based on your selected account type'
              ></i>
            </h2>
            <div className='text-gray-400 fw-bold fs-6'>
              {intl.formatMessage({id: 'MENU.CLASSE_INFORMATION_DSEC'})}
            </div>
          </div>

          <>
            <div className='row'>
              <div className='col-md-6 col-sm'>
                <Field name='establishements'>
                  {({form, field}: FieldProps) => (
                    <ReactSelectInput
                      label={
                        values.role === '3'
                          ? intl.formatMessage({id: 'SELECT.LABEL.ESTABLISHEMENTS'})
                          : intl.formatMessage({id: 'SELECT.LABEL.ESTABLISHEMENT'})
                      }
                      placeholder={
                        values.role === '3'
                          ? intl.formatMessage({id: 'SELECT.PLACEHOLDER.ESTABLISHEMENTS'})
                          : intl.formatMessage({id: 'SELECT.PLACEHOLDER.ESTABLISHEMENT'})
                      }
                      value={
                        values.role === '3'
                          ? getEstablishementsValue(values.establishements)
                          : getEstablishementsValue(values.establishements)[0]
                      }
                      options={establishements.map((establishement) => ({
                        label: establishement.name,
                        value: establishement._id!,
                      }))}
                      isMulti={values.role === '3' ? true : false}
                      onChange={(val: any) => {
                        if (val) {
                          values.role === '3'
                            ? setFieldValue(
                                'establishements',
                                val.map((v: any) => v.value)
                              )
                            : setFieldValue('establishements', [val.value])
                        } else {
                          setFieldValue('establishements', [])
                        }
                      }}
                      error={
                        form.touched[field.name] && form.errors[field.name]
                          ? (form.errors[field.name] as string)
                          : undefined
                      }
                    />
                  )}
                </Field>
              </div>
              <div className='col-md-6 col-sm'>
                <Field name='classrooms'>
                  {({form, field}: FieldProps) => (
                    <ReactSelectInput
                      label={
                        values.role === '3'
                          ? intl.formatMessage({id: 'SELECT.LABEL.CLASSROOMS'})
                          : intl.formatMessage({id: 'SELECT.LABEL.CLASSROOM'})
                      }
                      placeholder={
                        values.role === '3'
                          ? intl.formatMessage({id: 'SELECT.PLACEHOLDER.CLASSROOMS'})
                          : intl.formatMessage({id: 'SELECT.PLACEHOLDER.CLASSROOM'})
                      }
                      value={
                        values.role === '3'
                          ? getClassesValue(values.classrooms)
                          : getClassesValue(values.classrooms)[0]
                      }
                      options={classes.map((classe) => ({
                        label: classe.name,
                        value: classe._id!,
                      }))}
                      isMulti={values.role === '3' ? true : false}
                      onChange={(val: any) => {
                        if (val) {
                          values.role === '3'
                            ? setFieldValue(
                                'classrooms',
                                val.map((v: any) => v.value)
                              )
                            : setFieldValue('classrooms', [val.value])
                        } else {
                          setFieldValue('classrooms', [])
                        }
                      }}
                      error={
                        form.touched[field.name] && form.errors[field.name]
                          ? (form.errors[field.name] as string)
                          : undefined
                      }
                    />
                  )}
                </Field>
              </div>
              <div className='col-sm'>
                {values.role === '3' ? (
                  <Field name='subjects'>
                    {({form, field}: FieldProps) => (
                      <ReactSelectInput
                        label={intl.formatMessage({id: 'SELECT.LABEL.SUBJECTS'})}
                        placeholder={intl.formatMessage({id: 'SELECT.PLACEHOLDER.SUBJECTS'})}
                        value={getSubjectsValue(values.subjects)}
                        options={subjects.map((subject) => ({
                          label: subject.name,
                          value: subject._id!,
                        }))}
                        isMulti={true}
                        onChange={(val: any) => {
                          setFieldValue(
                            'subjects',
                            val.map((v: any) => v.value)
                          )
                        }}
                        error={
                          form.touched[field.name] && form.errors[field.name]
                            ? (form.errors[field.name] as string)
                            : undefined
                        }
                      />
                    )}
                  </Field>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <button
              style={{display: 'none'}}
              type='submit'
              ref={btnRef}
              onSubmit={() => handleSubmit()}
            />
          </>
          {error && (
            <>
              <div className='alert alert-dismissible bg-danger d-flex flex-column flex-sm-row p-5 my-10'>
                <div className='d-flex flex-column text-light pe-0 pe-sm-10'>
                  <span>{error}</span>
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default ClassInformation
